import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { injectJsonLd, SCHEMA_ORG } from "../lib/jsonld";
import Logo from "../components/Logo";
import { CalendarDots, ArrowRight, MagnifyingGlass, Rss, Eye, Flame } from "@phosphor-icons/react";

const CATEGORIES = [
  { id: "", label: "Toutes" },
  { id: "circuit_quebec", label: "Circuit Québec" },
  { id: "evenements", label: "Événements" },
  { id: "athletes", label: "Athlètes" },
  { id: "entrainement", label: "Entraînement" },
  { id: "nutrition", label: "Nutrition" },
  { id: "mental", label: "Mental" },
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function BlogPublic() {
  const [params, setParams] = useSearchParams();
  const category = params.get("categorie") || "";
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [topPosts, setTopPosts] = useState([]);

  useEffect(() => {
    document.title = "Blog | Institut Impact Élite Martial";
    // RSS auto-discovery
    let rssLink = document.querySelector('link[type="application/rss+xml"]');
    if (!rssLink) {
      rssLink = document.createElement("link");
      rssLink.setAttribute("rel", "alternate");
      rssLink.setAttribute("type", "application/rss+xml");
      rssLink.setAttribute("title", "Institut Impact Élite Martial — Blog");
      document.head.appendChild(rssLink);
    }
    rssLink.setAttribute("href", `${API}/blog/feed.xml`);
    // Schema.org Blog
    injectJsonLd("schema-blog", {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Institut Impact Élite Martial — Blog",
      "url": window.location.href,
      "description": "Réflexions, analyses et annonces du circuit québécois de Muay Thaï, Kickboxing et K-1.",
      "publisher": SCHEMA_ORG,
      "inLanguage": "fr-CA",
    });
    setLoading(true);
    const url = `${API}/blog/posts${category ? `?category=${category}` : ""}`;
    Promise.all([
      axios.get(url),
      axios.get(`${API}/blog/posts/top?limit=5`),
    ])
      .then(([listRes, topRes]) => {
        setPosts(listRes.data.items || []);
        setTotal(listRes.data.total || 0);
        setTopPosts(topRes.data.items || []);
      })
      .catch(() => { setPosts([]); setTotal(0); setTopPosts([]); })
      .finally(() => setLoading(false));
  }, [category]);

  const setCategory = (cat) => {
    if (cat) setParams({ categorie: cat });
    else setParams({});
  };

  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="hard-border-b sticky top-0 z-30 bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" data-testid="blog-home-link"><Logo variant="static" tone="dark" /></Link>
          <nav className="flex items-center gap-6 ticker">
            <Link to="/" className="hover:text-[#D90429]" data-testid="blog-nav-accueil">Accueil</Link>
            <Link to="/circuit" className="hover:text-[#D90429]" data-testid="blog-nav-circuit">Circuit</Link>
            <Link to="/blog" className="text-[#D90429] font-semibold" data-testid="blog-nav-blog">Blog</Link>
            <Link to="/auth" className="hard-border px-4 py-2 hover:bg-[#D90429] hover:text-white hover:border-[#D90429]" data-testid="blog-nav-login">Connexion</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="ticker text-[#D90429] mb-4">JOURNAL DU KRU</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-4">
            Le blog de l'<span className="text-[#D90429]">Institut</span>
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
            Réflexions, analyses et annonces du circuit québécois. Direct, sans filtre, signé Ralph David Jean-Pierre.
          </p>
        </div>
      </section>

      {/* Category filter */}
      <section className="hard-border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap gap-2" data-testid="blog-category-filter">
          {CATEGORIES.map(c => (
            <button
              key={c.id || "all"}
              onClick={() => setCategory(c.id)}
              data-testid={`blog-cat-${c.id || "all"}`}
              className={`px-4 py-2 ticker hard-border transition-colors ${
                (category || "") === c.id
                  ? "bg-[#D90429] text-white border-[#D90429]"
                  : "hover:bg-secondary"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </section>

      {/* Top read articles */}
      {topPosts.length > 0 && (
        <section className="border-b border-border/50 bg-secondary/30" data-testid="blog-top-section">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center gap-2 ticker text-[#D90429] mb-4">
              <Flame size={14} weight="fill" /> ARTICLES LES PLUS LUS
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {topPosts.map((p, idx) => (
                <Link
                  to={`/blog/${p.slug}`}
                  key={p.id}
                  data-testid={`blog-top-${idx + 1}`}
                  className="hard-border bg-background p-4 group hover:border-[#D90429] transition-colors flex gap-3"
                >
                  <div className="text-2xl font-extrabold text-[#D90429] leading-none">{idx + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold leading-snug mb-1 line-clamp-2 group-hover:text-[#D90429] transition-colors">
                      {p.title}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] ticker text-muted-foreground">
                      <Eye size={10} /> {p.views_count}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-muted-foreground ticker">Chargement…</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-24" data-testid="blog-empty">
            <MagnifyingGlass size={48} className="mx-auto text-muted-foreground mb-4" />
            <div className="text-xl font-bold mb-2">Aucun article pour cette catégorie</div>
            <div className="text-muted-foreground text-sm">Reviens bientôt — de nouveaux articles arrivent.</div>
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground ticker mb-6">{total} ARTICLE{total > 1 ? "S" : ""}</div>

            {/* Featured */}
            {featured && (
              <Link to={`/blog/${featured.slug}`} data-testid={`blog-card-${featured.slug}`} className="block hard-border mb-12 group hover:border-[#D90429] transition-colors">
                <div className="grid md:grid-cols-2 gap-0">
                  {featured.featured_image_url ? (
                    <div className="aspect-[4/3] md:aspect-auto bg-muted overflow-hidden">
                      <img src={featured.featured_image_url} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] md:aspect-auto bg-gradient-to-br from-[#D90429] to-black flex items-center justify-center">
                      <Logo variant="static" />
                    </div>
                  )}
                  <div className="p-8 md:p-12 flex flex-col justify-between">
                    <div>
                      <div className="ticker text-[#D90429] mb-3">{featured.category.replace("_", " ").toUpperCase()}</div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-3 leading-tight group-hover:text-[#D90429] transition-colors">{featured.title}</h2>
                      <p className="text-muted-foreground mb-6">{featured.excerpt}</p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <CalendarDots size={14} />
                        {formatDate(featured.published_at)}
                      </div>
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid of remaining */}
            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
                {rest.map(post => (
                  <Link to={`/blog/${post.slug}`} key={post.id} data-testid={`blog-card-${post.slug}`} className="hard-border block group hover:border-[#D90429] transition-colors">
                    {post.featured_image_url ? (
                      <div className="aspect-video bg-muted overflow-hidden">
                        <img src={post.featured_image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                    ) : (
                      <div className="aspect-video bg-gradient-to-br from-secondary to-muted" />
                    )}
                    <div className="p-6">
                      <div className="ticker text-[#D90429] mb-2 text-[10px]">{post.category.replace("_", " ").toUpperCase()}</div>
                      <h3 className="text-lg font-bold mb-2 leading-tight group-hover:text-[#D90429] transition-colors">{post.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-3">{post.excerpt}</p>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <CalendarDots size={12} />
                        {formatDate(post.published_at)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <footer className="hard-border-t mt-12">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 ticker text-muted-foreground">
          <div>INSTITUT IMPACT ÉLITE MARTIAL · VAL-D'OR, QC</div>
          <a href={`${API}/blog/feed.xml`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-[#D90429]" data-testid="blog-rss-link">
            <Rss size={14} /> FLUX RSS
          </a>
        </div>
      </footer>
    </div>
  );
}
